import { authedApiFetch } from 'utils/Api';

const salesforceTasksService = {
	getTasks: async (limit = 50) => {
		const response = await authedApiFetch({
			endpoint: '/salesforce_tasks',
			method: 'GET',
			params: { limit },
		});

		return response;
	},

	getFormOptions: async () => {
		return authedApiFetch({
			endpoint: '/salesforce_tasks/form_options',
			method: 'GET',
		});
	},

	// Generate a task-focused subject from the alert associated with the input signal
	suggestSubject: async (inputSignalId) => {
		return authedApiFetch({
			endpoint: '/salesforce_tasks/suggest_subject',
			method: 'POST',
			payload: {
				input_signal_id: inputSignalId,
			},
		});
	},

	createTask: async ({
		input_signal_id,
		subject,
		owner_id,
		status,
		priority,
		due_date,
		comments,
	}) => {
		const response = await authedApiFetch({
			endpoint: '/salesforce_tasks',
			method: 'POST',
			payload: {
				input_signal_id,
				subject,
				owner_id,
				status,
				priority,
				due_date,
				comments,
			},
			returnResponse: true,
		});

		if (!response.ok) {
			const body = await response.json();

			const message =
				getSalesforceErrorMessage(body?.error) ||
				'Unable to create Salesforce task.';

			const error = new Error(message);

			error.status = response.status;
			error.code = body?.error?.[0]?.errorCode;

			throw error;
		}

		return response.json();
	},

	updateTask: async (taskId, updates) => {
		const response = await authedApiFetch({
			endpoint: `/salesforce_tasks/${taskId}`,
			method: 'PATCH',
			payload: {
				task: updates,
			},
			returnResponse: true,
		});

		if (!response.ok) {
			const body = await response.json();

			const message =
				getSalesforceErrorMessage(body?.error) ||
				'Unable to update Salesforce task.';

			const error = new Error(message);

			error.status = response.status;
			error.code = body?.error?.[0]?.errorCode;

			throw error;
		}

		return response.json();
	},
};

export default salesforceTasksService;


// Normalize Salesforce API errors into user-friendly messages, providing
// specific guidance for known validation errors and falling back to the
// available backend message when no special handling is required
const getSalesforceErrorMessage = (error) => {
	if (typeof error === 'string') {
		return error;
	}

	if (Array.isArray(error)) {
		const firstError = error[0];

		if (
			firstError?.errorCode === 'MALFORMED_ID' &&
			firstError?.fields?.includes('OwnerId')
		) {
			return 'The selected user cannot be assigned tasks in Salesforce. Please choose another user.';
		}

		if (
			firstError?.errorCode === 'MALFORMED_ID' &&
			firstError?.fields?.includes('WhatId')
		) {
			return 'The related record is not valid in CRM. Please verify the alert is linked to a valid CRM record.';
		}

		const messages = error
			.map((item) => (typeof item === 'string' ? item : item?.message))
			.filter(Boolean);

		return messages.join(', ') || null;
	}

	if (error?.message) {
		return error.message;
	}

	return null;
};
